'use client'

import React, { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { ClipboardDocumentCheckIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { useToaster } from '../context/ToasterContext';
import Spinner from "../components/Spinner";
import { useSelector } from 'react-redux';
import api from '../api/axios';
import copyId from '../utils/copyId';
import { selectCurrentRole } from '../redux/globalDataSlice';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const INTERVALS = [
  { label: '1h',  minutes: 60,    bucketMinutes: 5  },
  { label: '4h',  minutes: 240,   bucketMinutes: 15 },
  { label: '8h',  minutes: 480,   bucketMinutes: 30 },
  { label: '1d',  minutes: 1440,  bucketMinutes: 60 },
  { label: '7d',  minutes: 10080, bucketMinutes: 360 },
];

const DEFAULT_INTERVAL = INTERVALS[3]; // 1d

/**
 * Format a bucket timestamp (epoch ms) in the browser's local timezone.
 * Mirrors the server's previous logic: HH:mm for ≤24 h windows, "Mon HH:mm" for 7d.
 */
function formatBucketLabel(timestampMs, intervalMinutes) {
  const d = new Date(timestampMs);
  if (intervalMinutes > 1440) {
    // 7-day window: show weekday + time, e.g. "Mon 06:00"
    return d.toLocaleString(undefined, {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
  // ≤24 h window: show time only, e.g. "14:00"
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg px-3 py-2">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
        <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
          {payload[0].value} messages
        </p>
      </div>
    );
  }
  return null;
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const { account = null, dataLoading } = useSelector((s) => s.globalData);
  const activeAccountId = useSelector((s) => s.globalData.activeAccountId);
  const [newSecret, setNewSecret] = useState(null);
  const [generatingSecret, setGeneratingSecret] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const role = useSelector(selectCurrentRole);
  const { showToast } = useToaster();
  const canGenerateSecret = ['ADMIN', 'DEVELOPER'].includes(role);

  const [selectedInterval, setSelectedInterval] = useState(DEFAULT_INTERVAL);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);

  useEffect(() => {
    setLoading(dataLoading);
  }, [dataLoading]);

  const fetchChartData = useCallback(async () => {
    if (!activeAccountId) return;
    setChartLoading(true);
    setChartError(null);
    try {
      const res = await api.get(
        `/mvapi/utils/correlation/account/${activeAccountId}/messages/count`,
        {
          params: {
            minutes: selectedInterval.minutes,
            bucketMinutes: selectedInterval.bucketMinutes,
          },
        }
      );
      // Format bucket timestamps in the browser's local timezone
      const formatted = res.data.map((bucket) => ({
        ...bucket,
        label: formatBucketLabel(bucket.timestamp, selectedInterval.minutes),
      }));
      setChartData(formatted);
    } catch (e) {
      setChartError('Failed to load chart data.');
    } finally {
      setChartLoading(false);
    }
  }, [activeAccountId, selectedInterval]);

  useEffect(() => {
    if (!loading) {
      fetchChartData();
    }
  }, [loading, fetchChartData]);

  const handleGenerateSecret = async () => {
    if (!account?.id) return;
    try {
      setGeneratingSecret(true);
      setNewSecret(null);
      const res = await api.post(`/mvapi/organization/${account.id}/generate-new`);
      setNewSecret(res.data.secret);
    } catch (e) {
      showToast('Failed to generate new secret', 'error');
    } finally {
      setGeneratingSecret(false);
    }
  };

  const handleCopyId = async (val, field) => {
    const ok = await copyId(val);
    if (ok) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1200);
    }
  };

  return (
    <>
      {loading && <Spinner />}

      {!loading && (
        <div className="px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
            Dashboard
          </h2>

          {/* Account Info */}
          <div className="mt-5">
            {account && (
              
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5" >
                {/* Row 1 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Name</label>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <div className="font-mono text-xs rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 shadow-sm w-80">
                      {account.name}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopyId(account?.name, 'name')}
                      className="inline-flex items-center rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                                 
                      aria-label="Copy Account Name"
                      title={copiedField === 'name' ? 'Copied!' : 'Copy Account Name'}
                    >
                      {copiedField === 'name' ? <ClipboardDocumentCheckIcon className="h-4 w-4" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account ID</label>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <div className="font-mono text-xs rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 shadow-sm w-80">
                      {account.id}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopyId(account?.id, 'id')}
                      className="inline-flex items-center rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                      aria-label="Copy Account ID"
                      title={copiedField === 'id' ? 'Copied!' : 'Copy Account ID'}
                    >
                      {copiedField === 'id' ? <ClipboardDocumentCheckIcon className="h-4 w-4" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Row 2 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Key</label>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <div className="font-mono text-xs rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 shadow-sm w-80">
                      {account.accountKey}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopyId(account?.accountKey, 'key')}
                      className="inline-flex items-center rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                      aria-label="Copy Account Key"
                      title={copiedField === 'key' ? 'Copied!' : 'Copy Account Key'}
                    >
                      {copiedField === 'key' ? <ClipboardDocumentCheckIcon className="h-4 w-4" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Secret</label>
                  <div className="flex items-center gap-2 flex-wrap text-gray-600 dark:text-gray-300">
                    <div className="font-mono text-xs rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 shadow-sm w-80">
                      {newSecret || '••••••••••••••••'}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopyId(newSecret, 'secret')}
                      className="inline-flex items-center rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                      aria-label="Copy Secret"
                      title={copiedField === 'secret' ? 'Copied!' : 'Copy Secret'}
                    >
                      {copiedField === 'secret' ? <ClipboardDocumentCheckIcon className="h-4 w-4" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={handleGenerateSecret}
                      disabled={generatingSecret || !canGenerateSecret}
                      className="flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-500 disabled:opacity-50"
                      title="Generate new secret"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                      Generate new secret
                    </button>
                    {newSecret && (
                      <span className="text-xs text-amber-500 dark:text-amber-400">
                        ⚠ Copy this now — it won't be shown again
                      </span>
                    )}
                  </div>
                </div>

                {/* Row 3 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Balance</label>
                  <div className="font-mono text-xs rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 shadow-sm w-80">
                    {account.balance ?? ''}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Credit Limit</label>
                  <div className="font-mono text-xs rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 shadow-sm w-80">
                    {account.creditLimit ?? ''}
                  </div>
                </div>

                {/* Row 4 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Suspended</label>
                  <div className="font-mono text-xs rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 shadow-sm w-80">
                    {account.suspended != null ? String(account.suspended) : ''}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Suspend Reason</label>
                  <div className="font-mono text-xs rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 shadow-sm w-80">
                    {account.suspendReason ?? 'None'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Messages Delivered Chart */}
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Messages Delivered</h3>
              <div className="flex items-center gap-2">
                {/* Interval buttons */}
                <div className="flex rounded-md shadow-sm border border-gray-300 dark:border-gray-700 overflow-hidden">
                  {INTERVALS.map((interval) => (
                    <button
                      key={interval.label}
                      type="button"
                      onClick={() => setSelectedInterval(interval)}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors
                        ${selectedInterval.label === interval.label
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }
                        border-r border-gray-300 dark:border-gray-700 last:border-r-0`}
                    >
                      {interval.label}
                    </button>
                  ))}
                </div>
                {/* Refresh button */}
                <button
                  type="button"
                  onClick={fetchChartData}
                  disabled={chartLoading}
                  className="inline-flex items-center rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition"
                  title="Refresh"
                >
                  <ArrowPathIcon className={`h-4 w-4 text-gray-500 dark:text-gray-400 ${chartLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            <div className="mt-3 rounded-lg bg-white dark:bg-black shadow px-6 py-5">
              {chartError ? (
                <div className="flex items-center justify-center h-48 text-sm text-red-500 dark:text-red-400">
                  {chartError}
                </div>
              ) : chartLoading && chartData.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-sm text-gray-400">
                  Loading…
                </div>
              ) : chartData.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-sm text-gray-400">
                  No data for this period.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-100 dark:text-gray-800" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      className="text-gray-500 dark:text-gray-400"
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={36}
                      className="text-gray-500 dark:text-gray-400"
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: '#10b981' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

        </div>
      )}
    </>
  );
}